import React, { useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

// What setup_assistant_bulk_confirm accepts in one request; a bigger card is
// sent in several.
const MAX_BULK_ACTIONS = 100;

/**
 * Setup Assistant bulk card: several same-kind proposals in one card with a
 * checkbox per item. Confirm runs the checked ones through the setup
 * confirm endpoint one by one and cancels the unchecked ones; each row
 * shows its own outcome.
 */
export default function SetupBulkActionCard({
  proposals,
  sessionUuid,
  homeStore,
  onStatusChange,
  onConfirmed,
  confirmUrl = API_ops_assistant_confirm_action,
  bulkConfirmUrl,
  scopeParams,
}) {
  const [selected, setSelected] = useState(() => new Set(proposals.map((p) => p.proposal_id)));
  const [rows, setRows] = useState(() =>
    Object.fromEntries(proposals.map((p) => [p.proposal_id, { status: p.status || "pending", error: null }]))
  );
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(proposals.every((p) => p.status && p.status !== "pending"));
  // How many rows failed on the most recent confirm attempt.
  const [lastFailedCount, setLastFailedCount] = useState(0);

  React.useEffect(() => {
    // The panel cancels pending cards when a new message is sent, and a
    // cancellation can resolve some rows and not others, so each row follows
    // its own proposal rather than waiting for the whole card.
    setRows((prev) => {
      let changed = false;
      const next = { ...prev };
      proposals.forEach((p) => {
        const outside = p.status === "rejected" ? "cancelled" : p.status;
        if ((outside === "cancelled" || outside === "confirmed") && next[p.proposal_id]?.status === "pending") {
          next[p.proposal_id] = { status: outside, error: null };
          changed = true;
        }
      });
      if (changed && Object.values(next).every((r) => r.status !== "pending")) setDone(true);
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals.map((p) => p.status).join(",")]);

  const title = proposals[0]?.summary?.title || proposals[0]?.tool_name || "Actions";
  const pending = proposals.filter((p) => rows[p.proposal_id]?.status === "pending");
  const checkedPending = pending.filter((p) => selected.has(p.proposal_id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const post = (proposal, action) =>
    postRightworkAPI(confirmUrl, {
      ...(scopeParams || { store_id: homeStore }),
      session_uuid: sessionUuid,
      proposal_id: proposal.proposal_id,
      action,
    });

  // One request for the whole card where the endpoint exists: a row per
  // request would spend the per-minute budget on a large team and leave the
  // rest of the rows failed.
  const postAll = (items) =>
    postRightworkAPI(bulkConfirmUrl, {
      ...(scopeParams || { store_id: homeStore }),
      session_uuid: sessionUuid,
      actions: items.map(({ proposal, action }) => ({
        proposal_id: proposal.proposal_id,
        action,
      })),
    });

  const run = async (confirmAll) => {
    setLoading(true);
    const confirmed = [];
    const cancelled = [];
    const next = { ...rows };
    const items = pending.map((proposal) => ({
      proposal,
      action: confirmAll && selected.has(proposal.proposal_id) ? "confirm" : "reject",
    }));
    const record = (proposal, action, result, error) => {
      if (error) {
        next[proposal.proposal_id] = { status: "failed", error };
        return;
      }
      // A rejection can fail too, when another tab confirmed the row first;
      // the server said no either way, so the row stays pending with its
      // error rather than reading as resolved.
      if (result && typeof result === "object" && result.error) {
        next[proposal.proposal_id] = { status: "failed", error: result.error };
        return;
      }
      next[proposal.proposal_id] = { status: action === "confirm" ? "confirmed" : "cancelled", error: null };
      (action === "confirm" ? confirmed : cancelled).push(proposal.proposal_id);
    };
    if (bulkConfirmUrl && items.length > 1) {
      for (let start = 0; start < items.length; start += MAX_BULK_ACTIONS) {
        const batch = items.slice(start, start + MAX_BULK_ACTIONS);
        try {
          const res = await postAll(batch);
          if (res.status !== 200) throw new Error("Failed to process actions.");
          const results = res?.data?.redux?.payload?.results || [];
          const byId = new Map(results.map((r) => [r.proposal_id, r.result]));
          batch.forEach(({ proposal, action }) => {
            const result = byId.get(proposal.proposal_id);
            record(proposal, action, result, result === undefined ? "Not processed." : null);
          });
        } catch (err) {
          const message =
            err?.response?.data?.system?.message || err?.response?.data?.error || err.message || "Failed.";
          batch.forEach(({ proposal }) => {
            next[proposal.proposal_id] = { status: "failed", error: message };
          });
        }
      }
    } else {
      for (const { proposal, action } of items) {
        try {
          const res = await post(proposal, action);
          if (res.status !== 200) throw new Error("Failed to process action.");
          record(proposal, action, res?.data?.redux?.payload?.result, null);
        } catch (err) {
          record(proposal, action, null,
            err?.response?.data?.system?.message || err?.response?.data?.error || err.message || "Failed.");
        }
      }
    }
    // A failed row stays pending (with its error) so it can be retried;
    // only rows that actually succeeded or were cancelled are resolved.
    // The failures are counted separately so the card can say how many
    // need attention: a pending row with an error is a failed attempt.
    const failed = Object.entries(next).filter(([, r]) => r.status === "failed");
    failed.forEach(([id, r]) => { next[id] = { status: "pending", error: r.error }; });
    setRows(next);
    setLastFailedCount(failed.length);
    if (confirmed.length) onStatusChange?.(confirmed, "confirmed");
    if (cancelled.length) onStatusChange?.(cancelled, "cancelled");
    if (failed.length) toast.error(`${failed.length} of ${pending.length} could not be completed. Fix and retry.`);
    const allResolved = Object.values(next).every((r) => r.status !== "pending");
    if (allResolved) setDone(true);
    setLoading(false);
    if (confirmed.length) onConfirmed?.(confirmed, [...confirmed, ...cancelled]);
  };

  const label = (proposal) => {
    const s = proposal.summary || {};
    return s.employee_name || s.store_name || s.role_name || s.type || s.title || proposal.tool_name;
  };
  const detail = (proposal) => {
    const s = proposal.summary || {};
    const parts = [s.employee_name && s.type, s.note].filter(Boolean);
    return parts.join(" · ");
  };

  if (done) {
    // Failed rows revert to pending and keep the card open, so a finished
    // card only ever holds confirmed and cancelled rows.
    const confirmedCount = Object.values(rows).filter((r) => r.status === "confirmed").length;
    const tone = confirmedCount
      ? "bg-green-50 border-green-200 text-green-700"
      : "bg-gray-50 border-gray-200 text-gray-500";
    return (
      <div className={`px-3 py-2 rounded-lg text-sm my-2 border ${tone}`}>
        <div className="flex items-center gap-2 font-medium">
          {confirmedCount ? <CheckCircleIcon fontSize="small" className="text-green-600" /> : <CancelIcon fontSize="small" />}
          <span>
            {title}: {confirmedCount} of {proposals.length} done
          </span>
        </div>
        <ul className="mt-1 space-y-0.5 text-xs">
          {proposals.map((p) => {
            const r = rows[p.proposal_id] || {};
            return (
              <li key={p.proposal_id} className="flex items-start gap-1.5">
                {r.status === "confirmed" ? (
                  <CheckCircleIcon fontSize="inherit" className="mt-0.5 text-green-600" />
                ) : r.status === "failed" ? (
                  <ErrorOutlineIcon fontSize="inherit" className="mt-0.5 text-amber-600" />
                ) : (
                  <CancelIcon fontSize="inherit" className="mt-0.5 text-gray-400" />
                )}
                <span>
                  {label(p)}
                  {r.status === "cancelled" && <span className="opacity-70"> (skipped)</span>}
                  {r.error && <span className="opacity-90">: {r.error}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <PlaylistAddCheckIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          {title} ({proposals.length})
        </span>
      </div>
      {lastFailedCount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200">
          <ErrorOutlineIcon fontSize="inherit" className="text-amber-600" />
          <span>
            {lastFailedCount} of {proposals.length} could not be completed. Fix the rows below and confirm again.
          </span>
        </div>
      )}
      <ul className="px-3 py-2 space-y-1">
        {pending.map((p) => (
          <li key={p.proposal_id}>
            <label className="flex items-start gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="accent-violet-700 mt-1"
                aria-label={label(p)}
                checked={selected.has(p.proposal_id)}
                disabled={loading}
                onChange={() => toggle(p.proposal_id)}
              />
              <span>
                <span className="font-medium">{label(p)}</span>
                {detail(p) && <span className="block text-xs text-gray-500">{detail(p)}</span>}
                {rows[p.proposal_id]?.error && (
                  <span role="alert" className="block text-xs text-red-600">{rows[p.proposal_id].error}</span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          type="button"
          onClick={() => run(false)}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => run(true)}
          disabled={loading || checkedPending.length === 0}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          Confirm {checkedPending.length}
        </button>
      </div>
    </div>
  );
}
